import constate from 'constate';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCliJobChecker } from 'cli/hooks';
import { ICliJob } from 'cli/types';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { QuoteVersionType } from 'quotes/enums';
import { useQuotePartActions } from 'quotes/hooks';

interface IProps {
  quoteId: number;
  quoteVersionType: QuoteVersionType;
  resetExpiredCostingCliJob?: ICliJob | null;
  expiredCostingAboveThreshold?: boolean;
  expiredCostingLinesCount?: number;
}

const useExpiredCosting = ({
  quoteId,
  quoteVersionType,
  resetExpiredCostingCliJob: initialResetExpiredCostingCliJob,
  expiredCostingAboveThreshold = false,
  expiredCostingLinesCount = 0,
}: IProps) => {
  const [resetExpiredCostingCliJob, setResetExpiredCostingCliJob] = useState<ICliJob | null>(
    initialResetExpiredCostingCliJob
  );
  const [displayResetExpiredCostsActionItem, setDisplayResetExpiredCostsActionItem] = useState(
    expiredCostingAboveThreshold
  );

  useEffect(() => {
    setDisplayResetExpiredCostsActionItem(expiredCostingAboveThreshold);
  }, [expiredCostingAboveThreshold]);

  useEffect(() => {
    setResetExpiredCostingCliJob(initialResetExpiredCostingCliJob);
  }, [initialResetExpiredCostingCliJob]);

  const [criteria, setCriteria] = useState<{ job: number } | null>(null);
  const [enabled, setEnabled] = useState(false);

  const { t } = useTranslation();

  const { updateRows } = useAgGridClientTableContext();
  const { clearNotification } = useNotificationContext();

  const { resetExpiredCosting } = useQuotePartActions();

  useEffect(() => {
    const initialResetExpiredCostingJobId = resetExpiredCostingCliJob?.id;

    if (!initialResetExpiredCostingJobId) {
      return;
    }

    setCriteria({ job: initialResetExpiredCostingJobId });
    setEnabled(true);
  }, [resetExpiredCostingCliJob?.id]);

  const onResetExpiredCosts = async () => {
    const { cliJob, success } = await resetExpiredCosting({
      quoteId,
      quoteVersionType,
    });

    if (success) {
      setDisplayResetExpiredCostsActionItem(false);

      if (!cliJob) {
        clearNotification();

        return;
      }

      setResetExpiredCostingCliJob(cliJob);
    }
  };

  const onResetExpiredCostingSuccess = ({ record }) => {
    const updatedRows = record?.result?.data;
    const chainedJob = record?.result?.chainedJob;

    updateRows({ update: updatedRows }, { refreshCells: true });

    if (chainedJob?.id) {
      setCriteria({ job: chainedJob.id });
      setEnabled(true);

      return;
    }

    setCriteria(null);
    setEnabled(false);
  };

  const onResetExpiredCostingError = () => {
    setCriteria(null);
    setEnabled(false);
    setDisplayResetExpiredCostsActionItem(true);
  };

  useCliJobChecker({
    criteria,
    enabled,
    onSuccess: onResetExpiredCostingSuccess,
    options: {
      loading: {
        title: t('titleStatus'),
        message: t('messageExpiredCostingResetInProgress'),
      },
      error: {
        title: t('errorTitleExpiredCostingReset'),
      },
      notificationPosition: NotificationPosition.TOP,
    },
    onError: onResetExpiredCostingError,
  });

  return {
    expiredCostingAboveThreshold,
    expiredCostingLinesCount,
    displayResetExpiredCostsActionItem,

    onResetExpiredCosts,
  };
};

export const [ExpiredCostingProvider, useExpiredCostingContext] = constate(useExpiredCosting);
